import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAutenticacao } from '../../context/autenticacao_contexto';
import {
    Typography,
    Container,
    Box,
    CircularProgress,
    Alert,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    Divider,
    IconButton,
    Avatar,
    ListItemAvatar,
    Fade,
    Button,
    useTheme
} from '@mui/material';
import {
    ArrowBack,
    Group,
    EventNote,
    Timeline as TimelineIcon,
} from '@mui/icons-material';
import { cores } from '../../theme';

const TurmaPainelPagina = () => {
    const { id: turmaId } = useParams();
    const navegar = useNavigate();
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const theme = useTheme();
    const { perfilUsuario } = useAutenticacao();

    const buscarDados = useCallback(async () => {
        try {
            const resposta = await api.get(`/api/turmas/${turmaId}/meu-painel`);
            setDados(resposta.data);
        } catch (err) {
            setErro('Falha ao carregar dados da turma.');
        } finally {
            setCarregando(false);
        }
    }, [turmaId]);

    useEffect(() => {
        buscarDados();
    }, [buscarDados]);

    const formatarData = (dataString) => {
        if (!dataString) return 'Não definida';
        const data = new Date(dataString);
        return data.toLocaleDateString('pt-BR');
    };

    if (carregando) {
        return (
            <Container maxWidth="lg" sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: cores.primaria.principal }} />
            </Container>
        );
    }

    if (erro) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert
                    severity="error"
                    sx={{
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                            alignItems: 'center'
                        }
                    }}
                >
                    {erro}
                </Alert>
            </Container>
        );
    }

    if (!dados) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert
                    severity="info"
                    sx={{
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                            alignItems: 'center'
                        }
                    }}
                >
                    Dados não encontrados.
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
            <Fade in={true} timeout={500}>
                <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, boxShadow: cores.sombras.medio }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 4,
                        p: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        background: cores.gradientes.primario,
                        color: 'white'
                    }}>
                        <IconButton
                            onClick={() => navegar('/')}
                            sx={{
                                mr: 2,
                                color: cores.fundo.padrao,
                                bgcolor: 'rgba(255,255,255,0.2)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                }
                            }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                {dados.turma && dados.turma.nome}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    opacity: 0.9,
                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    mt: 0.5
                                }}
                            >
                                <TimelineIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                                Painel de atividades da turma
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 3, px: { xs: 1, sm: 0 } }}>
                        <Typography
                            variant="h5"
                            component="h2"
                            sx={{
                                fontWeight: 700,
                                color: cores.primaria.principal,
                                mb: 1,
                                fontSize: { xs: '1.25rem', sm: '1.5rem' }
                            }}
                        >
                            Atividades Programadas
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: cores.cinza.escuro,
                                fontSize: { xs: '0.875rem', sm: '1rem' }
                            }}
                        >
                            Confira os casos clínicos disponíveis para sua turma
                        </Typography>
                    </Box>

                    {dados.gruposComAgendamentos.length === 0 ? (
                        <Box
                            sx={{
                                p: 4,
                                textAlign: 'center',
                                color: cores.cinza.escuro,
                                borderRadius: 2,
                                bgcolor: cores.fundo.claro,
                                border: `1px dashed ${cores.cinza.medio}`
                            }}
                        >
                            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                                Nenhuma atividade agendada
                            </Typography>
                            <Typography variant="body1">
                                Sua turma ainda não possui atividades programadas. Fique atento para novos agendamentos.
                            </Typography>
                        </Box>
                    ) : (
                        dados.gruposComAgendamentos.map((grupo, index) => (
                            <Fade in={true} timeout={300 + (index * 100)} key={grupo.id}>
                                <ListItemButton
                                    component={Paper}
                                    sx={{
                                        mb: 3,
                                        p: 0,
                                        display: 'block',
                                        borderRadius: 2,
                                        background: cores.fundo.claro,
                                        boxShadow: cores.sombras.suave,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: cores.sombras.medio,
                                        },
                                        overflow: 'hidden'
                                    }}
                                >
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        alignItems: { xs: 'stretch', sm: 'center' },
                                        justifyContent: 'space-between',
                                        p: { xs: 2, sm: 3 },
                                        borderBottom: '1px solid #e2e8f0',
                                        gap: { xs: 2, sm: 0 }
                                    }}>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: '100%'
                                        }}>
                                            <Avatar sx={{
                                                background: cores.gradientes.primario,
                                                mr: 2
                                            }}>
                                                <Group />
                                            </Avatar>
                                            <Box>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: cores.primaria.principal
                                                    }}
                                                >
                                                    {grupo.nome}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: cores.cinza.escuro
                                                    }}
                                                >
                                                    {grupo.agendamentos.length} {grupo.agendamentos.length === 1 ? 'atividade agendada' : 'atividades agendadas'}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {perfilUsuario?.cargo === 'PROFESSOR' && (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                component={RouterLink}
                                                to={`/relatorios/grupo/${grupo.id}`}
                                                startIcon={<TimelineIcon />}
                                                sx={{
                                                    background: cores.gradientes.primario,
                                                    color: 'white',
                                                    fontWeight: 500,
                                                    borderRadius: '8px',
                                                    boxShadow: cores.sombras.suave,
                                                    padding: '6px 16px',
                                                    minWidth: 'max-content',
                                                    '&:hover': {
                                                        background: cores.gradientes.secundario,
                                                        boxShadow: cores.sombras.medio,
                                                        transform: 'translateY(-1px)'
                                                    },
                                                    transition: 'all 0.2s ease',
                                                    alignSelf: { xs: 'flex-start', sm: 'center' },
                                                    ml: { sm: 2 },
                                                    whiteSpace: 'nowrap',
                                                    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                                    lineHeight: 1.5
                                                }}
                                            >
                                                Ver Relatório do Grupo
                                            </Button>
                                        )}
                                    </Box>

                                    <List sx={{ p: 0 }}>
                                        {grupo.agendamentos.length > 0 ? (
                                            grupo.agendamentos.map((ag, agIndex) => (
                                                <React.Fragment key={ag.id}>
                                                    {agIndex > 0 && <Divider />}
                                                    <ListItemButton
                                                        component={RouterLink}
                                                        to={`/grupos/${ag.grupo_tutoria_id}/casos/${ag.caso_clinico_id}`}
                                                        sx={{
                                                            py: 2,
                                                            px: { xs: 2, sm: 3 },
                                                            '&:hover': {
                                                                bgcolor: cores.fundo.claro,
                                                            }
                                                        }}
                                                    >
                                                        <ListItemAvatar>
                                                            <Avatar sx={{
                                                                bgcolor: `${cores.primaria.principal}15`,
                                                                color: cores.primaria.principal
                                                            }}>
                                                                <EventNote />
                                                            </Avatar>
                                                        </ListItemAvatar>
                                                        <ListItemText
                                                            primary={
                                                                <Typography variant="body1" sx={{ fontWeight: 600, color: cores.primaria.principal }}>
                                                                    {ag.caso_titulo}
                                                                </Typography>
                                                            }
                                                            secondary={
                                                                <Box sx={{ mt: 0.5 }}>
                                                                    <Typography variant="body2" sx={{ color: cores.cinza.escuro }}>
                                                                        {ag.periodo_nome}
                                                                    </Typography>
                                                                    <Typography variant="body2" sx={{ color: cores.cinza.medio, fontSize: '0.85rem', mt: 0.5 }}>
                                                                        Período: {formatarData(ag.data_inicio)} a {formatarData(ag.data_fim)}
                                                                    </Typography>
                                                                </Box>
                                                            }
                                                        />
                                                    </ListItemButton>
                                                </React.Fragment>
                                            ))
                                        ) : (
                                            <ListItem sx={{ py: 3 }}>
                                                <ListItemText
                                                    primary={
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                color: cores.cinza.escuro,
                                                                textAlign: 'center'
                                                            }}
                                                        >
                                                            Nenhuma atividade agendada para este grupo no momento.
                                                        </Typography>
                                                    }
                                                />
                                            </ListItem>
                                        )}
                                    </List>
                                </ListItemButton>
                            </Fade>
                        ))
                    )}
                </Paper>
            </Fade>
        </Container>
    );
};

export default TurmaPainelPagina;